import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { observer } from "mobx-react-lite";
import React, { useEffect } from "react";
import { reaction } from "mobx";
import ProductFilterRow from "./ProductFilterRow";
import { Button, Checkbox, message, Popover, Radio, Select, Tooltip } from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { useLocalStorageState } from "ahooks";
import {
    AggregatedOrderProductSelectState,
    getProductFilterOrderItemStatus,
    ProductInfoSortStrategy,
    ProductMergeRule,
} from "./store/ProductFilterStore";
import { AggregatedOrderProduct, AggregatedOrderProductCombineStatus } from "./type";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import OrderSortTipContent from "../OrderSortTipContent";

interface ProductFilterTableProps {
    showShopName?: boolean;
    size;
    tab: OrderTab;
}

enum Item {
    title = "title",
    skuProperty = "skuProperty",
    skuExternalId = "skuExternalId",
}

const PRODUCT_FILTER_TABLE_ITEMS = "__PRODUCT_FILTER_TABLE_ITEMS__";
const SELECTED_STRATEGY = "__SELECTED_STRATEGY__";
const PRODUCT_FILTER_SETTING_TOGGLE = "__PRODUCT_FILTER_SETTING_TOGGLE__";

enum SelectedStrategy {
    ORDER = "ORDER",
    ORDER_ITEM = "ORDER_ITEM",
}

const PRODUCT_MERGE_RULES = [
    ProductMergeRule.SAME_PRODUCT_SKU,
    ProductMergeRule.TITLE_AND_PROPERTIES,
    ProductMergeRule.SKU_EXTERNAL_ID,
    ProductMergeRule.SKU_SIMPLE_TITLE,
];

const ProductMergeRuleNameMap: Record<ProductMergeRule, string> = {
    [ProductMergeRule.SAME_PRODUCT_SKU]: "相同商品SKU合并展示",
    [ProductMergeRule.TITLE_AND_PROPERTIES]: "相同商品名称和属性合并展示",
    [ProductMergeRule.SKU_EXTERNAL_ID]: "相同SKU编码合并展示",
    [ProductMergeRule.SKU_SIMPLE_TITLE]: "相同SKU简称合并展示",
};

/**
 * 部分选的勾选单位。
 * 「勾选整个订单」时，一笔订单是一个单位。
 * 「只勾选这个商品」时，一个商品项是一个单位。
 */
interface PartialSelectUnit {
    orderId: number;
    /** 勾上或取消这个单位时，实际改动的商品项 */
    orderItemIds: number[];
    /** 勾上后，这一行「已选」件数会增加多少。商品项不能拆开勾，所以件数是整笔加上去的 */
    quantity: number;
}

/**
 * 从当前页靠前的单位开始，找出件数之和刚好等于 target 的一组。
 * 凑不出来时返回 null。target 为 0 表示这些单位一个都不选。
 */
function pickUnitsByQuantity(units: PartialSelectUnit[], target: number) {
    if (target === 0) return [];

    /** previousChoices[件数]：凑出该件数时最后用了哪个单位，以及用掉它之前还剩多少件 */
    const previousChoices: Array<{ previousSum: number; index: number } | undefined> = [];
    previousChoices[0] = { previousSum: -1, index: -1 };

    units.forEach((unit, index) => {
        const weight = unit.quantity;
        if (!Number.isInteger(weight) || weight <= 0 || weight > target) return;

        // 从大到小填，每个单位最多用一次。已经能凑出的件数不再覆盖，因此会优先用更靠前的订单
        for (let sum = target; sum >= weight; sum--) {
            if (previousChoices[sum] || !previousChoices[sum - weight]) continue;

            previousChoices[sum] = { previousSum: sum - weight, index };
        }
    });

    const pickedUnits: PartialSelectUnit[] = [];
    let sum = target;

    // 从目标件数倒推，把参与凑数的单位收集出来
    while (sum > 0) {
        const choice = previousChoices[sum];
        if (!choice) return null;

        pickedUnits.push(units[choice.index]);
        sum = choice.previousSum;
    }

    return pickedUnits;
}

const ProductMergeRuleTipMap: Record<ProductMergeRule, string> = {
    [ProductMergeRule.SAME_PRODUCT_SKU]:
        "简称（无简称则用名称）一致且SKU属性一致且商品编码一致且SKU编码一致时则合并展示",
    [ProductMergeRule.TITLE_AND_PROPERTIES]: "商品名称一致且属性一致时则合并展示",
    [ProductMergeRule.SKU_EXTERNAL_ID]: "只要SKU编码一致时则合并展示",
    [ProductMergeRule.SKU_SIMPLE_TITLE]: "只要SKU简称一致时则合并展示",
};

const ProductFilterTable: React.FC<ProductFilterTableProps> = function (props) {
    const { showShopName, tab } = props;
    const { table } = useOrderManagementStore();
    const [showSettings, updateShowSettings] = useLocalStorageState(PRODUCT_FILTER_SETTING_TOGGLE, {
        defaultValue: true,
    });
    const [shownItems, updateShownItems] = useLocalStorageState<Item[]>(PRODUCT_FILTER_TABLE_ITEMS, {
        defaultValue: [Item.skuProperty],
    });
    const [selectedStrategy, updateSelectedStrategy] = useLocalStorageState<SelectedStrategy>(SELECTED_STRATEGY, {
        defaultValue: SelectedStrategy.ORDER,
    });
    const productFilterOrderItemStatus = getProductFilterOrderItemStatus(tab);

    async function handleActiveChange(aggregatedProductId: string, isActive: boolean) {
        let hasJump = false;

        // 滚动到指定订单的元素
        const scrollElementIntoView = (orderId: number) => {
            const element = document.querySelector(`[data-order-id="${orderId}"]`);
            if (element) {
                element.scrollIntoView();
                hasJump = true;
            }
        };

        // 虚拟表格滚动并尝试聚焦元素
        const scrollToVirtualizedOrder = async (orderId: number) => {
            const orderIndex = table.orders?.findIndex((order) => order.id === orderId);
            if (orderIndex > -1) {
                let attempt = 0;

                // 循环尝试滚动到对应的 DOM 元素
                // 超过最大次数则放弃寻找
                while (attempt < 2 && !hasJump) {
                    table.virtualizedTableStore.scrollToIndex(orderIndex);

                    await new Promise((resolve) => {
                        requestAnimationFrame(() => {
                            attempt++;
                            scrollElementIntoView(orderId);
                            resolve(null);
                        });
                    });
                }
            }
        };

        const activateOrder = async (orderId: number) => {
            table.selectOrder(orderId);

            if (hasJump || table.orderLockStore.lockOrderIdMapping.get(orderId)) return;

            if (table.virtualizedTableStore) {
                await scrollToVirtualizedOrder(orderId);
            } else {
                scrollElementIntoView(orderId);
            }
        };

        const deactivateOrder = (orderId: number) => {
            table.unselectOrder(orderId);
        };

        const activateOrderItem = async (orderId: number, product: AggregatedOrderProduct) => {
            const order = table.orderMapping.get(orderId);

            if (!order) return;

            for (const orderItem of order.orderItems) {
                if (orderItem.status !== productFilterOrderItemStatus) continue;

                if (product.orderItemIds.includes(orderItem.id)) {
                    table.selectOrderItem(orderId, orderItem.id);
                }
            }

            if (hasJump || table.orderLockStore.lockOrderIdMapping.get(orderId)) return;

            if (table.virtualizedTableStore) {
                await scrollToVirtualizedOrder(orderId);
            } else {
                scrollElementIntoView(orderId);
            }
        };

        const deactivateOrderItem = (orderId: number, product: AggregatedOrderProduct) => {
            const order = table.orderMapping.get(orderId);

            if (!order) return;

            for (const orderItem of order.orderItems) {
                if (orderItem.status !== productFilterOrderItemStatus) continue;

                if (product.orderItemIds.includes(orderItem.id)) {
                    table.unselectOrderItem(orderId, orderItem.id);
                }
            }
        };

        const aggregatedOrderProduct = table.productFilterStore.aggregatedOrderProductMapping.get(aggregatedProductId);

        if (!aggregatedOrderProduct?.orderIds) return;

        for (const orderId of aggregatedOrderProduct.orderIds) {
            if (selectedStrategy === SelectedStrategy.ORDER) {
                isActive ? await activateOrder(orderId) : deactivateOrder(orderId);
            } else if (selectedStrategy === SelectedStrategy.ORDER_ITEM) {
                isActive
                    ? await activateOrderItem(orderId, aggregatedOrderProduct)
                    : deactivateOrderItem(orderId, aggregatedOrderProduct);
            }
        }
    }

    /**
     * 把这个商品在当前页的订单拆成可勾选单位。
     * 锁单不能改勾选，只记下它们已经选中的件数，后面从目标件数里扣掉。
     */
    function buildPartialSelectUnits(product: AggregatedOrderProduct) {
        const orderItemIdSet = new Set(product.orderItemIds);
        const selectableUnits: PartialSelectUnit[] = [];
        const allUnits: PartialSelectUnit[] = [];
        const lockedOrderIds = new Set<number>();
        let lockedSelectedQuantity = 0;

        product.orderIds.forEach((orderId) => {
            const order = table.orderMapping.get(orderId);
            // 和商品聚合时同一批：当前筛选状态、非赠品、属于这个商品
            const matchedItems = order?.orderItems?.filter((orderItem) => {
                if (orderItem.status !== productFilterOrderItemStatus) return false;
                if (orderItem.isGift) return false;

                return orderItemIdSet.has(orderItem.id);
            });

            if (!matchedItems?.length) return;

            if (table.orderLockStore.lockOrderIdMapping.get(orderId)) {
                lockedOrderIds.add(orderId);
                matchedItems.forEach((orderItem) => {
                    if (orderItem.isSelected) lockedSelectedQuantity += orderItem.quantity;
                });
                return;
            }

            // 勾选整个订单：一笔订单合成一个单位。只勾选这个商品：每个商品项单独一个单位
            const units =
                selectedStrategy === SelectedStrategy.ORDER
                    ? [
                          {
                              orderId,
                              orderItemIds: matchedItems.map((orderItem) => orderItem.id),
                              quantity: matchedItems.reduce((sum, orderItem) => sum + orderItem.quantity, 0),
                          },
                      ]
                    : matchedItems.map((orderItem) => ({
                          orderId,
                          orderItemIds: [orderItem.id],
                          quantity: orderItem.quantity,
                      }));

            allUnits.push(...units);
            selectableUnits.push(...units);
        });

        return { selectableUnits, allUnits, lockedOrderIds, lockedSelectedQuantity };
    }

    /**
     * 部分选：让这个商品的「已选」件数变成 count。
     * 返回 null 表示勾选已经按结果改完；返回字符串是输入框上的错误提示，此时不改勾选。
     */
    async function handlePartialSelect(aggregatedProductId: string, count: number) {
        const product = table.productFilterStore.aggregatedOrderProductMapping.get(aggregatedProductId);

        if (count > product.quantity) {
            return `请输入 0～${product.quantity} 之间的整数`;
        }

        const { selectableUnits, allUnits, lockedOrderIds, lockedSelectedQuantity } = buildPartialSelectUnits(product);

        // 锁单上已经选中的件数改不了，剩余件数再从能改的订单里凑
        if (lockedSelectedQuantity > count) return `这些订单的件数无法凑出 ${count} 件`;

        const pickedUnits = pickUnitsByQuantity(selectableUnits, count - lockedSelectedQuantity);

        if (!pickedUnits) return `这些订单的件数无法凑出 ${count} 件`;

        const pickedOrderIds = new Set(pickedUnits.map((unit) => unit.orderId));
        const pickedOrderItemIds = new Set(pickedUnits.flatMap((unit) => unit.orderItemIds));
        let hasJump = false;

        /** 只定位到第一笔新勾上的订单，避免连续滚动 */
        const scrollToOrder = async (orderId: number) => {
            if (hasJump || table.orderLockStore.lockOrderIdMapping.get(orderId)) return;

            const scrollElementIntoView = () => {
                const element = document.querySelector(`[data-order-id="${orderId}"]`);
                if (!element) return;

                element.scrollIntoView();
                hasJump = true;
            };

            if (!table.virtualizedTableStore) {
                scrollElementIntoView();
                return;
            }

            const orderIndex = table.orders?.findIndex((order) => order.id === orderId);
            if (orderIndex === undefined || orderIndex < 0) return;

            let attempt = 0;

            while (attempt < 2 && !hasJump) {
                table.virtualizedTableStore.scrollToIndex(orderIndex);

                await new Promise((resolve) => {
                    requestAnimationFrame(() => {
                        attempt++;
                        scrollElementIntoView();
                        resolve(null);
                    });
                });
            }
        };

        // 先取消没凑中的，再勾上凑中的。锁单不改
        if (selectedStrategy === SelectedStrategy.ORDER) {
            product.orderIds.forEach((orderId) => {
                if (lockedOrderIds.has(orderId) || pickedOrderIds.has(orderId)) return;

                table.unselectOrder(orderId);
            });

            for (const orderId of product.orderIds) {
                if (!pickedOrderIds.has(orderId)) continue;

                table.selectOrder(orderId);
                await scrollToOrder(orderId);
            }
        } else if (selectedStrategy === SelectedStrategy.ORDER_ITEM) {
            // 只动这个商品的商品项，同一订单里的其他商品保持原样
            allUnits.forEach((unit) => {
                unit.orderItemIds.forEach((orderItemId) => {
                    if (pickedOrderItemIds.has(orderItemId)) return;

                    table.unselectOrderItem(unit.orderId, orderItemId);
                });
            });

            allUnits.forEach((unit) => {
                unit.orderItemIds.forEach((orderItemId) => {
                    if (!pickedOrderItemIds.has(orderItemId)) return;

                    table.selectOrderItem(unit.orderId, orderItemId);
                });
            });

            for (const unit of allUnits) {
                if (!unit.orderItemIds.some((orderItemId) => pickedOrderItemIds.has(orderItemId))) continue;

                await scrollToOrder(unit.orderId);
                if (hasJump) break;
            }
        }

        return null;
    }

    function handleDisplayItemChange(changeItems: Item[]) {
        if (!changeItems.length) {
            message.error("至少选择一个选项");

            return;
        }

        updateShownItems(changeItems);
    }

    function handleSelectStrategyChange(e) {
        const value = e.target.value;
        updateSelectedStrategy(value);

        collectEvent(DataTrackEvent.CHANGE_ORDER_SELECT_STRATEGY, {
            content: value === SelectedStrategy.ORDER ? "勾选整个订单" : "只勾选这个商品",
        });
    }

    function handleProductInfoSortStrategyChange(e) {
        table.productFilterStore.setProductInfoSortStrategy(e.target.value);
    }

    useEffect(() => {
        return reaction(
            () => {
                const selectedQuantityMapping = new Map<string, number>();

                // 更新选中数量映射表
                const updateQuantityMapping = (key: string, quantity: number) => {
                    selectedQuantityMapping.set(key, (selectedQuantityMapping.get(key) || 0) + quantity);
                };

                table.orders?.forEach((order) => {
                    order.orderItems.forEach((orderItem) => {
                        if (!orderItem.isSelected) return;
                        if (orderItem.status !== productFilterOrderItemStatus) return;
                        if (orderItem.isGift) return;

                        /** 找到OrderItem对应的AggregateProduct */
                        const aggregateProduct = table.productFilterStore.orderItemProductMapping.get(orderItem.id);

                        if (!aggregateProduct) return;

                        if (aggregateProduct.combineStatus === AggregatedOrderProductCombineStatus.PARENT) {
                            /** 如果是合并父商品项，需要把该OrderItem对应的父商品项和子商品项一起更新 */
                            const children = aggregateProduct.childProducts.filter(({ orderItemIds }) =>
                                orderItemIds.includes(orderItem.id),
                            );
                            const effectProducts = [aggregateProduct, ...(children || [])];
                            effectProducts.forEach((product) => {
                                updateQuantityMapping(product.id, orderItem.quantity);
                            });
                        } else {
                            updateQuantityMapping(aggregateProduct.id, orderItem.quantity);
                        }
                    });
                });

                return { selectedQuantityMapping };
            },
            ({ selectedQuantityMapping }) => {
                const map = new Map<string, AggregatedOrderProductSelectState>();

                selectedQuantityMapping.forEach((selectedCount, id) => {
                    const aggregatedProduct = table.productFilterStore.aggregatedOrderProductMapping.get(id);

                    if (!aggregatedProduct) {
                        return;
                    }

                    const isFullSelected = aggregatedProduct.quantity === selectedCount;
                    const isPartSelected = !!(aggregatedProduct.quantity !== selectedCount && selectedCount);

                    map.set(id, { isFullSelected, isPartSelected, selectedCount });
                });

                table.productFilterStore.setSelectStateMapping(map);
            },
            {
                fireImmediately: true,
                /** 等待simpleProductMapping计算完成后再执行 */
                delay: 16,
            },
        );
    }, [productFilterOrderItemStatus, table]);

    function buildList() {
        const showSkuProperty = shownItems.includes(Item.skuProperty);
        const showSkuExternalId = shownItems.includes(Item.skuExternalId);
        const showTitle = shownItems.includes(Item.title);

        return table.productFilterStore.aggregatedOrderProducts.map((aggregatedProduct) => {
            return (
                <ProductFilterRow
                    showSkuProperty={showSkuProperty}
                    showSkuExternalId={showSkuExternalId}
                    showTitle={showTitle}
                    aggregatedProduct={aggregatedProduct}
                    key={aggregatedProduct.id}
                    onActiveChange={handleActiveChange}
                    showShopName={showShopName}
                    onPartialSelect={handlePartialSelect}
                />
            );
        });
    }

    const colgroup = (
        <colgroup>
            {showShopName ? (
                <>
                    <col style={{ width: "22%" }} />
                    <col />
                </>
            ) : (
                <col />
            )}
            <col style={{ width: 116 }} />
        </colgroup>
    );
    const productInfoSortTooltip = (
        <div className="small">
            <div>相同商品排在一起规则：</div>
            <div>商品级别：商品编码 &gt; 商品ID &gt; 商品标题</div>
            <div>SKU级别：SKU属性 &gt; SKU编码 &gt; SKU ID</div>
        </div>
    );
    const settingToggle = (
        <div className="p5 tc">
            <Button
                type="link"
                size="small"
                icon={showSettings ? <UpOutlined /> : <DownOutlined />}
                onClick={() => updateShowSettings(!showSettings)}
            >
                {showSettings ? "收起设置" : "更多设置"}
            </Button>
        </div>
    );

    return (
        <>
            <div className="content" style={{ overflow: "auto", border: "1px solid #dee2e6" }}>
                <table style={{ tableLayout: "fixed", width: "100%" }}>
                    {colgroup}
                    <thead>
                        <tr className="text-gray small product-filter-table-head">
                            {showShopName ? <td>店铺名称</td> : null}
                            <td>商品信息</td>
                            <td>已选/总数</td>
                        </tr>
                    </thead>
                    <tbody>{buildList()}</tbody>
                </table>
            </div>
            {showSettings ? (
                <>
                    <div className="p5 flex text-gray fs12 flex-center" style={{ flexWrap: "wrap" }}>
                        <div className="mr10">选择商品时:</div>
                        <Radio.Group onChange={handleSelectStrategyChange} value={selectedStrategy}>
                            <Radio value={SelectedStrategy.ORDER}>
                                <span className="small">勾选整个订单</span>
                            </Radio>
                            <Radio value={SelectedStrategy.ORDER_ITEM}>
                                <span className="small">只勾选这个商品</span>
                            </Radio>
                        </Radio.Group>
                    </div>

                    <div className="p5 flex text-gray fs12 flex-center" style={{ flexWrap: "wrap" }}>
                        <div className="mr10">商品信息排序:</div>
                        <Radio.Group
                            onChange={handleProductInfoSortStrategyChange}
                            value={table.productFilterStore.productInfoSortStrategy}
                        >
                            <Radio value={ProductInfoSortStrategy.QUANTITY_DESC}>
                                <span className="small">按商品总数倒序</span>
                            </Radio>
                            <Radio value={ProductInfoSortStrategy.SAME_PRODUCT}>
                                <Popover content={productInfoSortTooltip}>
                                    <span className="small product-filter-dashed-tip">相同商品排在一起</span>
                                </Popover>
                            </Radio>
                        </Radio.Group>
                    </div>

                    <div className="p5 flex text-gray fs12 flex-center" style={{ flexWrap: "wrap" }}>
                        <div className="mr10">商品信息展示项:</div>
                        <Checkbox.Group onChange={handleDisplayItemChange} value={shownItems}>
                            <Checkbox value={Item.title}>
                                <span className="small">商品简称</span>
                            </Checkbox>
                            <Checkbox value={Item.skuProperty}>
                                <span className="small">属性名称</span>
                            </Checkbox>
                            <Checkbox value={Item.skuExternalId}>
                                <span className="small">SKU编码</span>
                            </Checkbox>
                        </Checkbox.Group>
                    </div>

                    <div className="p5 flex text-gray fs12 flex-center" style={{ flexWrap: "wrap" }}>
                        <div className="mr10">商品信息合并规则:</div>
                        <Select<ProductMergeRule>
                            size="small"
                            style={{ width: 220 }}
                            popupMatchSelectWidth={false}
                            optionLabelProp="optionLabel"
                            value={table.productFilterStore.mergeRule}
                            onChange={(rule) => {
                                table.productFilterStore.updateMergeRule(rule);
                            }}
                            options={[
                                ...(PRODUCT_MERGE_RULES.map((rule) => {
                                    const label = ProductMergeRuleNameMap[rule];
                                    return {
                                        key: rule,
                                        value: rule,
                                        label: (
                                            <Tooltip
                                                color="white"
                                                destroyOnHidden
                                                styles={{
                                                    root: {
                                                        maxWidth: 320,
                                                    },
                                                    container: {
                                                        color: "#333",
                                                    },
                                                }}
                                                title={
                                                    <OrderSortTipContent
                                                        title={label}
                                                        rule={ProductMergeRuleTipMap[rule]}
                                                    />
                                                }
                                                placement="left"
                                            >
                                                {label}
                                            </Tooltip>
                                        ),
                                        optionLabel: label,
                                    };
                                }) ?? []),
                            ]}
                        />
                    </div>

                    {settingToggle}
                </>
            ) : (
                settingToggle
            )}
        </>
    );
};

export default observer(ProductFilterTable);
